/**
 * <mux-bg data-mux="PLAYBACK_ID"><video muted loop playsinline preload="none"></video></mux-bg>
 *
 * Ambient, muted, looping Mux background video.
 *
 * This ships inside the closing CTA, which lives in the FOOTER group -- i.e. it renders on EVERY page.
 * So it is deliberately frugal, and the rules below are the whole point of the file:
 *
 *   - NO poster attribute. A `poster` URL is fetched as soon as the <video> lays out, regardless of
 *     preload="none" (this is exactly what was silently downloading two Mux thumbnails on every
 *     homepage load -- see film-band.js/revealPoster). The section's own lazy <img> already sits
 *     behind this element as the still frame, so a poster here would be a duplicate download.
 *   - preload="none" + first-scroll gate + IntersectionObserver: nothing is requested until the
 *     visitor has scrolled at all AND the footer is within 4000px. Most visits never scroll that far,
 *     so most visits pay nothing at all.
 *   - hls.js (~1MB) is fetched only when genuinely needed: in view, motion allowed, data allowed, and
 *     no native HLS (Safari plays .m3u8 directly and never loads the library).
 *   - reduced motion / save-data / 2g: never load. The poster image IS the experience; a looping
 *     video is motion the visitor did not ask for.
 *   - playback pauses when scrolled away or the tab is backgrounded, so it never burns CPU unseen.
 *
 * Wrapped in an IIFE: a section can render twice (Section Rendering API), and a top-level `class`
 * would throw on the second evaluation. See reference: duplicate-section script.
 */
(() => {
  const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js';

  /**
   * One-shot "the visitor is actually going somewhere" signal, shared by every instance.
   *
   * This is what pays for the wide load margin below. Margin alone cannot go wide here: the closing
   * CTA is in the footer group, so on a short page the footer starts within any generous rootMargin
   * and the stream would be fetched on load, on every page, for every visitor -- including the ones
   * who bounce above the fold. Waiting for the first scroll keeps the "most visits pay nothing"
   * property intact while letting the margin roughly double.
   *
   * Any scroll counts, not a distance threshold: on a short page there may be no room to scroll a
   * threshold's worth, and the video would then never load at all.
   */
  const scrollWaiters = [];
  let hasScrolled = window.scrollY > 0;   // already scrolled: back-nav restore, or an #anchor landing
  const onFirstScroll = (fn) => {
    if (hasScrolled) fn();
    else scrollWaiters.push(fn);
  };
  if (!hasScrolled) {
    const fire = () => {
      hasScrolled = true;
      window.removeEventListener('scroll', fire);
      scrollWaiters.splice(0).forEach((fn) => fn());
    };
    window.addEventListener('scroll', fire, { passive: true, once: true });
  }

  // One shared fetch of the library across every instance on the page AND across files. The promise
  // is parked on window, not in this closure: film-band.js needs the same ~1MB library, and two
  // private closures meant two <script> elements for the same URL. The HTTP cache spared the second
  // download, but both elements still EXECUTE -- ~1MB parsed and evaluated twice on the main thread,
  // on the homepage, where both players exist. Whichever file gets there first now wins and the other
  // awaits the same promise. Safari never enters here at all (native HLS).
  const loadHlsLib = () =>
    (window.__zumaHlsLib ||= new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = HLS_CDN;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    }));

  class MuxBg extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      // NB: `playbackId`, not `id` -- `this.id` is the element's DOM id property. Same class of trap as
      // `this.hidden`, which used to stamp the hidden attribute and blank the hero.
      this.playbackId = this.dataset.mux;
      if (!this.video || !this.playbackId) return;

      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Bail before observing: no stream, no library, no bytes. CSS hides us under reduced motion, so
      // the poster image behind is what shows.
      if (this.motion.matches || this.saveData()) {
        // Tell the 2.2.2 control it has nothing to control. Reduced motion is handled in CSS, but
        // save-data/2g is not a media query, so without this signal the visitor got a "Pause background
        // video" button floating over a poster on EVERY page (this section is in the footer group) --
        // and pressing it announced "Play background video" for a stream that never existed.
        this.video.__ambientUnavailable = true;
        document.querySelectorAll('ambient-pause').forEach((el) => el.refresh?.());
        return;
      }

      this.tabHidden = document.hidden;
      this.onVisibility = () => {
        this.tabHidden = document.hidden;
        this.sync();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      // Two observers, because "start loading early" and "play only when seen" want different margins
      // (Joey: start loading before it is in view, no blank frame, and already at quality on arrival).
      //   - load at 4000px out: ~4 viewports of runway, so the stream is attached, ABR has settled off
      //     its opening estimate, and a full-quality frame is decoded well before arrival.
      //   - play/pause on actual visibility, so nothing burns CPU while off-screen.
      //
      // Armed only after the first scroll -- see onFirstScroll above. That gate is what lets this run
      // wider than the film band's 2600 despite being sitewide: an above-the-fold bounce never arms the
      // observer, so it still never touches Mux. Buffering this far ahead is bounded by
      // maxMaxBufferLength (30s), so the early start costs runway, not unbounded bytes.
      onFirstScroll(() => {
        if (!this.isConnected || this.loaded) return;

        // Warm the LIBRARY as soon as the visitor commits to scrolling, decoupled from the stream.
        // Joey asked to start hls even sooner; the honest answer is that the margin was never the
        // bottleneck -- the ~1MB library was, because it only began downloading at the moment the
        // stream was needed, putting a full fetch+parse on the critical path right when the band was
        // about to arrive. Warming it here on idle means that by the time the 4000px trigger fires,
        // window.Hls already exists and attaching is instant.
        // Costs nothing extra: same one shared promise, and Safari never enters (native HLS). Gated on
        // first scroll and requestIdleCallback so a bounce still pays nothing and it never competes
        // with LCP.
        // Warm hls.js whenever it will be used: see the native-HLS note in play() below.
        if ((window.MediaSource || window.ManagedMediaSource) || !this.video.canPlayType('application/vnd.apple.mpegurl')) {
          const warm = () => loadHlsLib().catch(() => {});
          if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
          else setTimeout(warm, 1200);
        }

        this.loadIo = new IntersectionObserver(
          ([entry]) => { if (entry.isIntersecting) { this.loadIo.disconnect(); this.load(); } },
          { rootMargin: '4000px 0px' }
        );
        this.loadIo.observe(this);
      });

      this.io = new IntersectionObserver(
        ([entry]) => { this.onscreen = entry.isIntersecting; this.sync(); },
        { rootMargin: '0px' }
      );
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.loadIo?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.hls?.destroy();
    }

    /** Respect the visitor's data preferences: a decorative loop is never worth their bandwidth. */
    saveData() {
      const c = navigator.connection;
      return !!c && (c.saveData === true || /(^|-)2g$/.test(c.effectiveType || ''));
    }

    async load() {
      this.loaded = true;
      // rendition_order=desc puts the highest rendition FIRST in the manifest. This is the lever that
      // matters for the native path (Safari, and current Chrome -- which reports canPlayType 'maybe'
      // and really does play HLS): native players generally start on the first variant, so the default
      // ascending order makes them open at the bottom of the ladder and climb. Desc opens at the top.
      // The ladder still adapts from there; this only changes where it starts. (Mux docs: modify
      // playback behavior -- rendition_order / max_resolution / min_resolution.)
      // rendition_order=desc + min_resolution=720p. Verified against the live manifest: the ladder is
      // 1080@9.2, 1080@6.9, 720@3.6, 540@2.3, 360@1.2, and min_resolution=720p leaves the top three.
      //
      // Narrowing beats "asking for higher". Mux caches SEGMENTS for 7 days
      // (must-revalidate, max-age=604800) but the manifest for none, so a repeat visit re-runs ABR from
      // scratch -- and the cache only hits if it lands on the SAME rung it did last time. Five rungs
      // means it wanders and re-downloads. Three means it usually doesn't, and this section is in the
      // footer group, so that saving repeats on every page of the site.
      // It also sets a quality FLOOR: the loop can no longer open at 360p soft behind the copy.
      //
      // The cost, stated: a slow-but-not-save-data connection now stalls where it used to degrade to
      // 360p. Acceptable here specifically because this is decorative and muted -- the poster holds and
      // nothing is lost but motion -- and because save-data/2g already bail before this line. Do NOT
      // copy this onto a video someone actually chose to watch.
      const src = `https://stream.mux.com/${this.playbackId}.m3u8?rendition_order=desc&min_resolution=720p`;
      // Buffer ahead once we've committed to loading, so a frame is decoded before the band scrolls in
      // and there is never a blank. preload="none" in the markup is what keeps it free until this point.
      this.video.preload = 'auto';

      // 2026-09-24: built-in HLS only when the browser has NO Media Source Extensions (older iPhones).
      // Chrome 14x now answers canPlayType('application/vnd.apple.mpegurl') with 'maybe' but fails
      // to play these Mux streams (MEDIA_ERR_SRC_NOT_SUPPORTED), so trusting canPlayType broke the
      // film on Chrome. hls.js plays them wherever MSE exists (Chrome, Firefox, Edge, desktop Safari,
      // iOS 17.1+ via ManagedMediaSource); that is also hls.js's own recommended order.
      if (!(window.MediaSource || window.ManagedMediaSource) && this.video.canPlayType('application/vnd.apple.mpegurl')) {
        this.video.src = src;                       // native HLS (Safari, and current Chrome)
      } else {
        try {
          await loadHlsLib();
        } catch {
          return;                                   // CDN blocked/offline: poster image still holds
        }
        if (!window.Hls?.isSupported() || !this.isConnected) return;
        // NOT capLevelToPlayerSize: that caps the ladder to the element's box and is exactly why the
        // film band leaves it at its false default ("so quality isn't capped low"). This is a
        // full-bleed background, so the box IS the viewport and capping just costs quality.
        //
        // startLevel -1 = let ABR choose, but ABR's opening guess is abrEwmaDefaultEstimate, which
        // defaults to 500kbps and therefore opens at the BOTTOM of the ladder on a cold load. See the
        // long note in film-band.js: rendition_order=desc only steers native players, not this path.
        // 5Mbps assumes broadband until real throughput measures otherwise (a guess, not a cap).
        this.hls = new window.Hls({ maxMaxBufferLength: 30, startLevel: -1, abrEwmaDefaultEstimate: 5_000_000 });
        this.hls.loadSource(src);
        this.hls.attachMedia(this.video);
      }
      this.sync();
    }

    /** Play only while actually visible, and only if the visitor has not paused it. */
    sync() {
      if (!this.loaded) return;
      // __ambientPaused is set by the 2.2.2 pause control (assets/ambient-pause.js). This check has to
      // live here rather than in the control alone: sync() re-plays on every scroll and visibility
      // change, so without it the video would resume the moment the visitor scrolled.
      const shouldPlay = this.onscreen && !this.tabHidden && !this.video.__ambientPaused;
      if (shouldPlay) this.video.play?.().catch(() => {});
      else this.video.pause?.();
    }
  }

  if (!customElements.get('mux-bg')) customElements.define('mux-bg', MuxBg);
})();
